import React, { useEffect, useState, createElement, Component } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, AlertTriangle } from 'lucide-react';
import { signupFormsApi, formConfigsApi, emailTemplatesApi, newslettersApi, extractLookupId, SignupFormRecord } from '../services/creatorApi';
// Duplicate of the logic in EmbedCodes.tsx/SignupFormEditor.tsx
// Ideally this would be shared, but keeping it self-contained for now to avoid refactoring risk
interface EnrichmentData {
  emailHtml: string;
  emailSubject: string;
  newsletterId: string;
  region: string;
  redirectUrl: string;
  appOrigin: string;
  confirmationLanguage?: string;
}
// Conditional routing rule with pre-fetched template content
interface RoutingRuleData {
  field: string;
  operator: 'equals' | 'not_equals' | 'contains';
  value: string;
  emailHtml: string;
  emailSubject: string;
}
export interface WelcomeScreenConfig {
  enabled: boolean;
  title: string;
  description: string;
  buttonText: string;
  imageUrl: string;
  estimatedTime: string;
  backgroundColor: string;
  textColor: string;
}
function escapeForJSString(str: string): string {
  return str.replace(/\\/g, '\\\\').replace(/'/g, "\\'").replace(/\n/g, '\\n').replace(/\r/g, '\\r').replace(/<\/script>/gi, '<\\/script>');
}
function generateSubmissionScript(team: string, slug: string, enrichment?: EnrichmentData | null, successMessage?: string, submittingText?: string, errorMessage?: string, validationErrorMessage?: string, postSubmitRedirectUrl?: string, routingRules?: RoutingRuleData[], logicRules?: any[]): string {
  console.log('[generateSubmissionScript][PublicFormEmbed]', {
    team,
    slug,
    enrichment: enrichment ? {
      emailHtml: enrichment.emailHtml?.substring(0, 80),
      emailSubject: enrichment.emailSubject,
      rulesCount: enrichment.rules?.length
    } : null,
    hasEnrichment: !!enrichment,
    hasEmailHtml: !!enrichment?.emailHtml,
    hasRules: !!enrichment?.rules?.length,
    routingRulesCount: routingRules?.length,
    caller: new Error().stack?.split('\n')[2]?.trim()
  });
  var safeTeam = team.replace(/'/g, "\\'").replace(/\\/g, '\\\\');
  var safeSuccessMsg = escapeForJSString(successMessage || 'Successfully submitted! Please check your email to confirm.');
  var safeSubmittingText = escapeForJSString(submittingText || 'Enviando…');
  var safeErrorMsg = escapeForJSString(errorMessage || 'Error al enviar. Inténtalo de nuevo.');
  var safeValidationMsg = escapeForJSString(validationErrorMessage || 'Por favor, complete todos los campos obligatorios.');
  var safePostRedirect = postSubmitRedirectUrl ? escapeForJSString(postSubmitRedirectUrl) : '';
  var safeEmailHtml = enrichment ? escapeForJSString(enrichment.emailHtml) : '';
  var safeEmailSubject = enrichment ? escapeForJSString(enrichment.emailSubject) : '';
  var safeNewsletterId = enrichment ? escapeForJSString(enrichment.newsletterId) : '';
  var safeRegion = enrichment ? escapeForJSString(enrichment.region) : '';
  var safeRedirectUrl = enrichment ? escapeForJSString(enrichment.redirectUrl) : '';
  var safeAppOrigin = enrichment ? escapeForJSString(enrichment.appOrigin) : '';
  var safeLang = enrichment?.confirmationLanguage ? escapeForJSString(enrichment.confirmationLanguage) : '';
  var langSuffix = safeLang ? "+'&lang=" + safeLang + "'" : '';
  var lines: string[] = [];
  lines.push('(function(){');
  lines.push("  var form=document.querySelector('form');");
  lines.push('  if(!form)return;');
  // Inline message function — appears near the submit button at the bottom of the form
  lines.push('  function showToast(msg,isErr){');
  lines.push("    var t=document.getElementById('_ft52');if(t)t.remove();");
  lines.push("    t=document.createElement('div');t.id='_ft52';");
  lines.push("    t.style.cssText='margin:12px 0 4px;padding:12px 16px;border-radius:8px;font-size:14px;font-weight:500;font-family:-apple-system,BlinkMacSystemFont,sans-serif;display:flex;align-items:center;gap:10px;'+(isErr?'background:#fef2f2;color:#dc2626;border:1px solid #fecaca':'background:#f0fdf4;color:#16a34a;border:1px solid #bbf7d0');");
  lines.push("    var ic=document.createElement('span');");
  lines.push("    ic.textContent=isErr?'\\u26A0':'\\u2713';");
  lines.push("    ic.style.cssText='font-size:16px;flex-shrink:0';");
  lines.push("    var sp=document.createElement('span');sp.textContent=msg;sp.style.cssText='flex:1';");
  lines.push("    var cl=document.createElement('button');cl.innerHTML='&times;';");
  lines.push("    cl.style.cssText='background:none;border:none;color:inherit;opacity:0.6;font-size:18px;cursor:pointer;padding:0;line-height:1;margin-left:4px;flex-shrink:0';");
  lines.push('    cl.onclick=function(){if(t&&t.parentNode)t.remove();};');
  lines.push('    t.appendChild(ic);t.appendChild(sp);t.appendChild(cl);');
  // Insert after submit button, or at end of form
  lines.push('    var _btn=form.querySelector(\'[type="submit"],button:not([type])\');');
  lines.push('    if(_btn&&_btn.parentNode){_btn.parentNode.insertBefore(t,_btn.nextSibling);}');
  lines.push('    else{form.appendChild(t);}');
  lines.push('    try{t.scrollIntoView({behavior:"smooth",block:"nearest"});}catch(e){}');
  lines.push('    setTimeout(function(){if(t&&t.parentNode)t.remove();},8000);');
  lines.push('  }');
  // Validation helper
  lines.push('  function validateForm(){');
  lines.push('    // Only validate visible required fields');
  lines.push("    var rq=form.querySelectorAll('[required]');");
  lines.push('    for(var i=0;i<rq.length;i++){');
  lines.push('      var el=rq[i];');
  lines.push('      // Skip if hidden by logic engine');
  lines.push('      var block=el.closest("[data-block-id]");');
  lines.push('      if(block && block.style.display==="none") continue;');
  lines.push("      if(el.type==='checkbox'){if(!el.checked)return false;}");
  lines.push("      else if(el.type==='radio'){");
  lines.push("        var g=form.querySelectorAll('input[name=\"'+el.name+'\"]');");
  lines.push('        var ok=false;for(var j=0;j<g.length;j++){if(g[j].checked){ok=true;break;}}');
  lines.push('        if(!ok)return false;');
  lines.push('      }');
  lines.push('      else{if(!el.value||!String(el.value).trim())return false;}');
  lines.push('    }');
  lines.push('    return true;');
  lines.push('  }');
  // --- Runtime Logic Engine ---
  if (logicRules && logicRules.length > 0) {
    const rulesJson = JSON.stringify(logicRules).replace(/<\/script>/gi, '<\\/script>');
    lines.push('  var logicRules=' + rulesJson + ';');
    lines.push('  var initialStates={};');
    lines.push('  var initialSelectOptions={};');
    lines.push('  function initLogic(){');
    lines.push('    var blocks=form.querySelectorAll("[data-block-id]");');
    lines.push('    for(var i=0;i<blocks.length;i++){');
    lines.push('      var b=blocks[i];');
    lines.push('      var id=b.getAttribute("data-block-id");');
    lines.push('      var reqs=[];');
    lines.push('      var inputs=b.querySelectorAll("input,select,textarea");');
    lines.push('      for(var j=0;j<inputs.length;j++){');
    lines.push('        if(inputs[j].hasAttribute("required")) reqs.push(inputs[j]);');
    lines.push('      }');
    lines.push('      initialStates[id]={ display: b.style.display, reqs: reqs };');
    lines.push('      var sel=b.querySelector("select");');
    lines.push('      if(sel){');
    lines.push('        var opts=[];');
    lines.push('        for(var s=0;s<sel.options.length;s++) opts.push(sel.options[s].cloneNode(true));');
    lines.push('        initialSelectOptions[id]=opts;');
    lines.push('      }');
    lines.push('    }');
    lines.push('  }');
    lines.push('  function evaluateLogic(){');
    lines.push('    // Reset all to initial');
    lines.push('    for(var id in initialStates){');
    lines.push('      var b=form.querySelector("[data-block-id=\'"+id+"\']");');
    lines.push('      if(!b)continue;');
    lines.push('      b.style.display=initialStates[id].display;');
    lines.push('      var inputs=b.querySelectorAll("input,select,textarea");');
    lines.push('      for(var j=0;j<inputs.length;j++) inputs[j].removeAttribute("required");');
    lines.push('      // Reset select options to original');
    lines.push('      if(initialSelectOptions[id]){');
    lines.push('        var sel=b.querySelector("select");');
    lines.push('        if(sel){');
    lines.push('          var curVal=sel.value;');
    lines.push('          sel.innerHTML="";');
    lines.push('          for(var s=0;s<initialSelectOptions[id].length;s++) sel.appendChild(initialSelectOptions[id][s].cloneNode(true));');
    lines.push('          sel.value=curVal;');
    lines.push('        }');
    lines.push('      }');
    lines.push('      for(var k=0;k<initialStates[id].reqs.length;k++) initialStates[id].reqs[k].setAttribute("required","");');
    lines.push('    }');
    lines.push('    // Evaluate rules');
    lines.push('    for(var i=0;i<logicRules.length;i++){');
    lines.push('      var rule=logicRules[i];');
    lines.push('      var matchCount=0;');
    lines.push('      for(var j=0;j<rule.conditions.length;j++){');
    lines.push('        var cond=rule.conditions[j];');
    lines.push('        var triggerBlock=form.querySelector("[data-block-id=\'"+cond.fieldId+"\']");');
    lines.push('        if(!triggerBlock)continue;');
    lines.push('        var input=triggerBlock.querySelector("input,select,textarea");');
    lines.push('        if(!input)continue;');
    lines.push('        var val="";');
    lines.push('        if(input.type==="checkbox"||input.type==="radio"){');
    lines.push('          var checked=triggerBlock.querySelector("input:checked");');
    lines.push('          val=checked?checked.value:"";');
    lines.push('        }else{ val=input.value; }');
    lines.push('        val=(val||"").trim().toLowerCase();');
    lines.push('        var targetVal=(cond.value||"").trim().toLowerCase();');
    lines.push('        var m=false;');
    lines.push('        switch(cond.operator){');
    lines.push('          case "equals": m=(val===targetVal); break;');
    lines.push('          case "not_equals": m=(val!==targetVal); break;');
    lines.push('          case "contains": m=(val.indexOf(targetVal)!==-1); break;');
    lines.push('          case "not_contains": m=(val.indexOf(targetVal)===-1); break;');
    lines.push('          case "is_empty": m=(val===""); break;');
    lines.push('          case "is_not_empty": m=(val!==""); break;');
    lines.push('          case "greater_than": m=(parseFloat(val)>parseFloat(targetVal)); break;');
    lines.push('          case "less_than": m=(parseFloat(val)<parseFloat(targetVal)); break;');
    lines.push('          case "one_of": m=(targetVal.split(",").map(function(s){return s.trim()}).indexOf(val)!==-1); break;');
    lines.push('        }');
    lines.push('        if(m) matchCount++;');
    lines.push('      }');
    lines.push('      var isMatch = rule.conditionMatch==="any" ? matchCount>0 : matchCount===rule.conditions.length;');
    lines.push('      if(isMatch){');
    lines.push('        for(var k=0;k<rule.actions.length;k++){');
    lines.push('          var act=rule.actions[k];');
    lines.push('          var target=form.querySelector("[data-block-id=\'"+act.targetId+"\']");');
    lines.push('          if(!target)continue;');
    lines.push('          if(act.type==="show_block") target.style.display="block";');
    lines.push('          else if(act.type==="hide_block") target.style.display="none";');
    lines.push('          else if(act.type==="require_block"){');
    lines.push('            var ins=target.querySelectorAll("input,select,textarea");');
    lines.push('            for(var l=0;l<ins.length;l++) ins[l].setAttribute("required","");');
    lines.push('          }');
    lines.push('          else if(act.type==="optional_block"){');
    lines.push('            var ins=target.querySelectorAll("input,select,textarea");');
    lines.push('            for(var l=0;l<ins.length;l++) ins[l].removeAttribute("required");');
    lines.push('          }');
    lines.push('          else if(act.type==="set_field_value"){');
    lines.push('            var inp=target.querySelector("input,select,textarea");');
    lines.push('            if(inp) inp.value=act.value||"";');
    lines.push('          }');
    lines.push('          else if(act.type==="jump_to_block"){');
    lines.push('            var inp=target.querySelector("input,select,textarea");');
    lines.push('            if(inp) inp.focus();');
    lines.push('          }');
    lines.push('          else if(act.type==="filter_options"&&act.filterValues){');
    lines.push('            var sel=target.querySelector("select");');
    lines.push('            if(sel){');
    lines.push('              var tid=target.getAttribute("data-block-id");');
    lines.push('              var origOpts=initialSelectOptions[tid];');
    lines.push('              if(origOpts){');
    lines.push('                var curVal=sel.value;');
    lines.push('                sel.innerHTML="";');
    lines.push('                var fv=act.filterValues;');
    lines.push('                for(var f=0;f<origOpts.length;f++){');
    lines.push('                  var ov=origOpts[f].value;');
    lines.push('                  if(!ov||fv.indexOf(ov)!==-1) sel.appendChild(origOpts[f].cloneNode(true));');
    lines.push('                }');
    lines.push('                if(fv.indexOf(curVal)!==-1) sel.value=curVal; else sel.selectedIndex=0;');
    lines.push('              }');
    lines.push('            }');
    lines.push('          }');
    lines.push('        }');
    lines.push('      }');
    lines.push('    }');
    lines.push('  }');
    lines.push('  initLogic();');
    lines.push('  form.addEventListener("change", evaluateLogic);');
    lines.push('  form.addEventListener("input", evaluateLogic);');
    lines.push('  evaluateLogic();');
  }
  // --- End Runtime Logic Engine ---
  lines.push('  function genToken(){');
  lines.push('    var ts=Date.now().toString(36);');
  lines.push("    var r='';for(var i=0;i<16;i++)r+=Math.floor(Math.random()*36).toString(36);");
  lines.push("    return ts+'_'+r;");
  lines.push('  }');
  lines.push('  function renderTpl(tpl,vars){');
  lines.push('    var result=tpl;');
  lines.push('    for(var k in vars){');
  lines.push('      if(!vars.hasOwnProperty(k))continue;');
  lines.push("      var re=new RegExp('\\\\{\\\\{\\\\s*'+k.replace(/[.*+?^${}()|[\\]\\\\]/g,'\\\\$&')+'\\\\s*\\\\}\\\\}','gi');");
  lines.push("      result=result.replace(re,vars[k]||'');");
  lines.push('    }');
  lines.push('    return result;');
  lines.push('  }');
  lines.push("  form.addEventListener('submit',function(e){");
  lines.push('    e.preventDefault();');
  lines.push('    e.stopImmediatePropagation();');
  // Call custom JS validation hook if defined (e.g. from user's custom JavaScript)
  lines.push('    if(typeof window._52sBeforeSubmit==="function"){');
  lines.push('      if(window._52sBeforeSubmit()===false)return;');
  lines.push('    }else{');
  // Fallback: use built-in validation only if no custom hook
  lines.push("      if(!validateForm()){showToast('" + safeValidationMsg + "',true);return;}");
  lines.push('    }');
  lines.push('    var fd=new FormData(form);');
  lines.push('    var fields={};');
  lines.push('    fd.forEach(function(v,k){fields[k]=v;});');
  lines.push('    if(Object.keys(fields).length===0){');
  lines.push("      var inputs=form.querySelectorAll('input,select,textarea');");
  lines.push('      for(var i=0;i<inputs.length;i++){');
  lines.push('        var el=inputs[i];');
  lines.push("        var n=el.getAttribute('name')||el.getAttribute('id')||'';");
  lines.push("        if(n&&el.type!=='submit'&&el.type!=='button'){");
  lines.push("          if(el.type==='checkbox')fields[n]=el.checked?'yes':'no';");
  lines.push("          else if(el.type==='radio'){if(el.checked)fields[n]=el.value;}");
  lines.push("          else fields[n]=el.value||el.textContent||'';");
  lines.push('        }');
  lines.push('      }');
  lines.push('    }');
  // Capture URL parameters and merge into fields object.
  // First try window.location.search (works when params are on the iframe URL).
  // Then fall back to document.referrer (the parent page URL — works inside
  // cross-origin iframes like WordPress embeds where the iframe URL has no params).
  lines.push('    try{');
  lines.push('      var _qs=window.location.search;');
  lines.push('      if(!_qs&&document.referrer){try{_qs=new URL(document.referrer).search}catch(_e2){}}');
  lines.push('      if(_qs){');
  lines.push('        var _sp=new URLSearchParams(_qs);');
  lines.push('        _sp.forEach(function(v,k){');
  lines.push("          if(k==='token'||k==='id'||k==='lang')return;");
  // If a field with this name exists but is empty, overwrite it
  lines.push('          if(k in fields){if(!fields[k])fields[k]=v;}');
  // Otherwise add it directly (no prefix) AND with _url_ prefix
  lines.push("          else{fields[k]=v;fields['_url_'+k]=v;}");
  lines.push('        });');
  lines.push('      }');
  lines.push('    }catch(_e){}');
  lines.push('    var btn=form.querySelector(\'[type="submit"],button:not([type])\');');
  lines.push('    var origText="";');
  lines.push('    if(btn){origText=btn.textContent;btn.disabled=true;btn.textContent="' + safeSubmittingText + '";}');
  lines.push("    var email=fields.email||fields.Email||fields.email_address||fields.EMAIL||fields.emailAddress||fields.e_mail||'';");
  lines.push('    var token=genToken();');
  if (enrichment && enrichment.appOrigin) {
    lines.push("    var confirmUrl='" + safeAppOrigin + "/confirm?token='+encodeURIComponent(token)" + langSuffix + ';');
  } else {
    lines.push("    var confirmUrl=window.location.origin+'/confirm?token='+encodeURIComponent(token)" + langSuffix + ';');
  }
  // --- Conditional email routing ---
  // Embed the default template
  if (enrichment && enrichment.emailHtml) {
    lines.push("    var defaultHtml='" + safeEmailHtml + "';");
    lines.push("    var defaultSubject='" + safeEmailSubject + "';");
  } else {
    lines.push("    var defaultHtml='';");
    lines.push("    var defaultSubject='';");
  }
  // Embed routing rules (if any)
  if (routingRules && routingRules.length > 0) {
    // Serialize rules as a JSON array embedded in the script
    const rulesJson = JSON.stringify(routingRules.map(r => ({
      field: r.field,
      op: r.operator,
      val: r.value,
      html: r.emailHtml,
      subject: r.emailSubject
    })));
    lines.push('    var _rules=' + rulesJson + ';');
    // Evaluate rules against form fields to pick the right template
    lines.push('    console.log("[PublicFormEmbed] Evaluating "+_rules.length+" routing rules against fields:",JSON.stringify(fields));');
    lines.push('    var tplHtml=defaultHtml,tplSubject=defaultSubject;');
    lines.push('    for(var ri=0;ri<_rules.length;ri++){');
    lines.push('      var _r=_rules[ri],_fv=(fields[_r.field]||"").trim().toLowerCase(),_rv=(_r.val||"").trim().toLowerCase(),_m=false;');
    lines.push("      if(_r.op==='equals')_m=(_fv===_rv);");
    lines.push("      else if(_r.op==='not_equals')_m=(_fv!==_rv);");
    lines.push("      else if(_r.op==='contains')_m=(_fv.indexOf(_rv)!==-1);");
    lines.push('      console.log("[PublicFormEmbed] Rule "+ri+": field="+_r.field+" fVal=\\""+_fv+"\\" op="+_r.op+" rVal=\\""+_rv+"\\" → match="+_m);');
    lines.push('      if(_m&&_r.html){tplHtml=_r.html;tplSubject=_r.subject;console.log("[PublicFormEmbed] ✅ Rule "+ri+" MATCHED — subject: "+_r.subject);break;}');
    lines.push('    }');
  } else {
    lines.push('    var tplHtml=defaultHtml,tplSubject=defaultSubject;');
    lines.push('    console.log("[PublicFormEmbed] No routing rules — using default template. defaultHtml length:",defaultHtml.length);');
  }
  // Render the selected template with form field values
  lines.push('    var tplVars={};');
  lines.push('    for(var fk in fields){if(fields.hasOwnProperty(fk))tplVars[fk]=fields[fk];}');
  lines.push('    tplVars.confirmation_link=confirmUrl;');
  lines.push("    tplVars.team_name='" + safeTeam + "';");
  lines.push('    var renderedHtml=renderTpl(tplHtml,tplVars);');
  lines.push('    var renderedSubject=renderTpl(tplSubject,tplVars);');
  lines.push('    console.log("[PublicFormEmbed] Final rendered — subject:",renderedSubject.substring(0,80),"htmlLength:",renderedHtml.length);');
  // Detect consent checkbox state at submission time
  lines.push('    var consentChecked=false;');
  lines.push('    var consentInputs=form.querySelectorAll(\'input[type="checkbox"]\');');
  lines.push('    for(var ci=0;ci<consentInputs.length;ci++){');
  lines.push("      var cn=(consentInputs[ci].name||'').toLowerCase();");
  lines.push("      if(cn.indexOf('consent')!==-1 && consentInputs[ci].checked){consentChecked=true;break;}");
  lines.push('    }');
  // Determine status: if no opt-in email, auto-confirm regardless of consent field presence
  const hasOptInEmail = !!(enrichment && (enrichment.emailHtml || enrichment.rules && enrichment.rules.length > 0));
  if (hasOptInEmail) {
    lines.push("    var submitStatus='Not Subscribed';");
  } else {
    lines.push("    var submitStatus='Confirmed';");
  }
  lines.push('    var payload={');
  lines.push('      Form_Payload:JSON.stringify(fields),');
  lines.push('      Status:submitStatus,');
  lines.push("      Team:'" + safeTeam + "',");
  lines.push('      Subscriber_Email:email,');
  lines.push('      Confirmation_Token:token,');
  if (enrichment && enrichment.newsletterId) lines.push("      Newsletter_ID:'" + safeNewsletterId + "',");
  if (enrichment && enrichment.region) lines.push("      Region:'" + safeRegion + "',");
  if (enrichment && enrichment.redirectUrl) lines.push("      Redirect_URL:'" + safeRedirectUrl + "',");
  lines.push('      Rendered_Email_HTML:renderedHtml,');
  lines.push('      Rendered_Email_Subject:renderedSubject,');
  lines.push('      Added_Time:new Date().toISOString()');
  lines.push('    };');
  lines.push("    fetch('https://www.websiteforge.com/custom/chosen/chosen_zoho_api.php',{");
  lines.push("      method:'POST',");
  lines.push("      headers:{'Content-Type':'application/json','API-KEY':'ud7^3jf(80'},");
  lines.push('      body:JSON.stringify({');
  lines.push("        url:'https://www.zohoapis.com/creator/v2.1/data/52studios/campaigns/form/Submissions',");
  lines.push("        method:'POST',");
  lines.push('        data:{data:payload}');
  lines.push('      })');
  lines.push('    })');
  lines.push('    .then(function(r){return r.json();})');
  lines.push('    .then(function(data){');
  // After successful POST, extract record ID and re-render email with &id= param
  // This ensures the confirmation link includes the record ID for reliable lookup
  lines.push('      var recordId=data&&data.data&&data.data.ID?String(data.data.ID):"";');
  lines.push('      if(recordId&&tplHtml){');
  lines.push('        var fullConfirmUrl=confirmUrl+"&id="+encodeURIComponent(recordId);');
  lines.push('        var updVars={};');
  lines.push('        for(var uk in fields){if(fields.hasOwnProperty(uk))updVars[uk]=fields[uk];}');
  lines.push('        updVars.confirmation_link=fullConfirmUrl;');
  lines.push("        updVars.team_name='" + safeTeam + "';");
  lines.push('        var updHtml=renderTpl(tplHtml,updVars);');
  lines.push('        var updSubject=renderTpl(tplSubject,updVars);');
  lines.push("        fetch('https://www.websiteforge.com/custom/chosen/chosen_zoho_api.php',{");
  lines.push("          method:'POST',");
  lines.push("          headers:{'Content-Type':'application/json','API-KEY':'ud7^3jf(80'},");
  lines.push('          body:JSON.stringify({');
  lines.push("            url:'https://www.zohoapis.com/creator/v2.1/data/52studios/campaigns/report/All_Submissions/'+recordId,");
  lines.push("            method:'PATCH',");
  lines.push('            data:{data:{Rendered_Email_HTML:updHtml,Rendered_Email_Subject:updSubject}}');
  lines.push('          })');
  lines.push('        }).catch(function(){});');
  lines.push('      }');
  if (postSubmitRedirectUrl) {
    lines.push("      window.location.href='" + safePostRedirect + "';");
  } else {
    // Call custom JS after-submit hook if defined (e.g. show share modal, custom success UI)
    lines.push('      if(typeof window._52sAfterSubmit==="function"){');
    lines.push('        try{window._52sAfterSubmit();}catch(_e){}');
    lines.push('      }else{');
    lines.push("        showToast('" + safeSuccessMsg + "',false);");
    lines.push('      }');
    lines.push('      if(btn){btn.disabled=false;btn.textContent=origText;}');
    lines.push('      form.reset();');
  }
  lines.push('    })');
  lines.push('    .catch(function(err){');
  lines.push("      showToast('" + safeErrorMsg + "',true);");
  lines.push('      if(btn){btn.disabled=false;btn.textContent=origText;}');
  lines.push("      console.error('Form submission error:',err);");
  lines.push('    });');
  lines.push('  },true);');
  lines.push('})();');
  return lines.join('\n');
}
export function PublicFormEmbed() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [record, setRecord] = useState<SignupFormRecord | null>(null);
  const [scriptContent, setScriptContent] = useState('');
  const [welcomeConfig, setWelcomeConfig] = useState<WelcomeScreenConfig | null>(null);
  const [showForm, setShowForm] = useState(false);
  useEffect(() => {
    if (!slug) return;
    const loadForm = async () => {
      try {
        setLoading(true);
        // Retry logic for public-facing forms — Creator API can be transiently unavailable
        let form: SignupFormRecord | null = null;
        let lastApiError: Error | null = null;
        const MAX_RETRIES = 3;
        const RETRY_DELAYS = [0, 2000, 4000];
        for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
          if (attempt > 0) {
            await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt]));
          }
          try {
            form = await signupFormsApi.findBySlug(slug);
            lastApiError = null;
            break; // Success — exit retry loop
          } catch (err) {
            lastApiError = err instanceof Error ? err : new Error(String(err));
            console.warn(`[PublicFormEmbed] Attempt ${attempt + 1}/${MAX_RETRIES} failed:`, err);
          }
        }
        // Distinguish "API error" from "form genuinely not found"
        if (lastApiError) {
          console.error('[PublicFormEmbed] All retries failed:', lastApiError);
          setError('Temporarily unavailable. Please refresh the page to try again.');
          return;
        }
        if (!form) {
          setError('Form not found');
          return;
        }
        if (form.Status === 'archived' || form.Status === 'draft') {
          setError('This form is not active');
          return;
        }
        setRecord(form);
        // Load enrichment data
        let enrichment: EnrichmentData | null = null;
        let routingRules: RoutingRuleData[] = [];
        let successMessage = 'Successfully submitted! Please check your email to confirm.';
        let submittingText = 'Enviando…';
        let errorMessage = 'Error al enviar. Inténtalo de nuevo.';
        let validationErrorMessage = 'Por favor, complete todos los campos obligatorios.';
        let postSubmitRedirectUrl = '';
        // Extract settings and routing rules from Builder_Schema
        let emailRoutingRules: any[] = [];
        let confirmationLanguage = '';
        let logicRules: any[] = [];
        if (form.Builder_Schema) {
          try {
            const schema = JSON.parse(form.Builder_Schema);
            if (schema.successMessage) successMessage = schema.successMessage;
            if (schema.submittingText) submittingText = schema.submittingText;
            if (schema.errorMessage) errorMessage = schema.errorMessage;
            if (schema.validationErrorMessage) validationErrorMessage = schema.validationErrorMessage;
            if (schema.postSubmitRedirectUrl) postSubmitRedirectUrl = schema.postSubmitRedirectUrl;
            if (schema.confirmationLanguage) confirmationLanguage = schema.confirmationLanguage;
            if (schema.welcomeScreen?.enabled) {
              setWelcomeConfig(schema.welcomeScreen);
            } else {
              setShowForm(true);
            }
            if (schema.rules) {
              logicRules = schema.rules;
            }
            // Extract routing rules for later processing
            if (schema.emailRoutingRules && Array.isArray(schema.emailRoutingRules)) {
              emailRoutingRules = schema.emailRoutingRules;
            }
          } catch {}
        } else {
          setShowForm(true);
        }
        try {
          // 1. Find Form Configuration
          const configs = await formConfigsApi.findByFormId(form.ID);
          if (configs.length > 0) {
            const config = configs[0];
            const templateId = extractLookupId(config.Email_Template);
            if (templateId) {
              // 2. Fetch Default Email Template
              const template = await emailTemplatesApi.get(templateId);
              if (template) {
                // 3. Find Newsletter
                let newsletterId = '';
                try {
                  const allNewsletters = await newslettersApi.list();
                  const newsletter = allNewsletters.find(nl => {
                    const nlFormId = extractLookupId(nl.Sign_Up_Form);
                    return nlFormId === form.ID;
                  });
                  if (newsletter) newsletterId = newsletter.Newsletter_ID || '';
                } catch {}
                // 4. Extract redirect URL
                let redirectUrl = '';
                const rawLink = (template as any).Confirmation_Link;
                if (rawLink) {
                  redirectUrl = typeof rawLink === 'string' ? rawLink : rawLink?.url || '';
                }
                enrichment = {
                  emailHtml: template.Email_HTML_Body || '',
                  emailSubject: template.Email_Subject || '',
                  newsletterId,
                  region: config.Region || template.Region || '',
                  redirectUrl,
                  appOrigin: window.location.origin,
                  confirmationLanguage: confirmationLanguage || undefined
                };
              }
            }
          }
          // 5. Fetch templates for conditional routing rules
          if (emailRoutingRules.length > 0) {
            for (const rule of emailRoutingRules) {
              if (rule.templateId && rule.field && rule.value) {
                try {
                  const ruleTemplate = await emailTemplatesApi.get(rule.templateId);
                  if (ruleTemplate) {
                    routingRules.push({
                      field: rule.field,
                      operator: rule.operator || 'equals',
                      value: rule.value,
                      emailHtml: ruleTemplate.Email_HTML_Body || '',
                      emailSubject: ruleTemplate.Email_Subject || ''
                    });
                  }
                } catch (err) {
                  console.warn(`Failed to fetch template for routing rule (field: ${rule.field}):`, err);
                }
              }
            }
          }
        } catch (err) {
          console.warn('Failed to load enrichment data:', err);
        }
        // Generate the submission script
        const script = generateSubmissionScript(form.Team || '', slug, enrichment, successMessage, submittingText, errorMessage, validationErrorMessage, postSubmitRedirectUrl, routingRules, logicRules);
        setScriptContent(script);
      } catch (err) {
        console.error('Error loading form:', err);
        setError('Failed to load form');
      } finally {
        setLoading(false);
      }
    };
    loadForm();
  }, [slug]);
  // Inject script after render
  useEffect(() => {
    if (!loading && !error && record && scriptContent && showForm) {
      // Clean up previous scripts if any
      const existingScript = document.getElementById('form-submission-script');
      if (existingScript) existingScript.remove();
      const existingUserScript = document.getElementById('form-user-script');
      if (existingUserScript) existingUserScript.remove();
      // 1. Inject a URL-param pre-population script FIRST.
      // Inside an iframe, window.location.search is empty, but document.referrer
      // contains the parent page URL (e.g. https://osescolhidos.tv/pascoa/?utm_content=jesus.net).
      // This script reads params from the referrer and fills matching hidden fields
      // so that the user's custom JS (captureUtmContent) and our submission script both work.
      const existingPrepop = document.getElementById('form-prepop-script');
      if (existingPrepop) existingPrepop.remove();
      const prepopScript = document.createElement('script');
      prepopScript.id = 'form-prepop-script';
      prepopScript.textContent = `(function(){try{var qs=window.location.search;if(!qs&&document.referrer){try{qs=new URL(document.referrer).search}catch(e){}}if(!qs)return;var sp=new URLSearchParams(qs);var f=document.querySelector('form');if(!f)return;sp.forEach(function(v,k){if(k==='token'||k==='id'||k==='lang')return;var el=f.querySelector('input[name="'+k+'"]')||document.getElementById(k);if(el&&(el.type==='hidden'||!el.value)){el.value=v;}});}catch(e){}})();`;
      document.body.appendChild(prepopScript);
      // 2. Inject user's custom JS so hooks (_52sBeforeSubmit, _52sAfterSubmit)
      // are defined before our submission script runs
      if (record.JavaScript1) {
        const userScript = document.createElement('script');
        userScript.id = 'form-user-script';
        userScript.textContent = record.JavaScript1;
        document.body.appendChild(userScript);
      }
      // 3. Then inject our submission script (which calls the hooks)
      const script = document.createElement('script');
      script.id = 'form-submission-script';
      script.textContent = scriptContent;
      document.body.appendChild(script);
      return () => {
        const s0 = document.getElementById('form-prepop-script');
        if (s0) s0.remove();
        const s1 = document.getElementById('form-submission-script');
        if (s1) s1.remove();
        const s2 = document.getElementById('form-user-script');
        if (s2) s2.remove();
      };
    }
  }, [loading, error, record, scriptContent, showForm]);
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-transparent" data-id="element-5591">
        <Loader2 className="h-8 w-8 text-blue-500 animate-spin" data-id="element-5592" />
      </div>;
  }
  if (error || !record) {
    const isApiError = error?.includes('Temporarily') || error?.includes('refresh');
    return <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4" data-id="element-5593">
        <div className="text-center max-w-md" data-id="element-5594">
          <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${isApiError ? 'bg-yellow-100' : 'bg-red-100'}`} data-id="element-5595">
            <AlertTriangle className={`h-6 w-6 ${isApiError ? 'text-yellow-600' : 'text-red-600'}`} data-id="element-5596" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-5597">
            {isApiError ? 'Temporarily Unavailable' : 'Unavailable'}
          </h3>
          <p className="text-gray-500 mb-4" data-id="element-5598">{error || 'Form not found'}</p>
          {isApiError && <button onClick={() => window.location.reload()} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors" data-id="element-5599">
              Try Again
            </button>}
        </div>
      </div>;
  }
  const handleStartForm = () => {
    setShowForm(true);
  };
  return <>
      <style data-id="element-5600">{`
        body { margin: 0; padding: 0; background: ${welcomeConfig ? welcomeConfig.backgroundColor : 'transparent'}; }
        /* Reset basic styles for embed consistency */
        * { box-sizing: border-box; }
        ${record.CSS || ''}
      `}</style>

      {welcomeConfig && !showForm && <div className="min-h-screen flex flex-col items-center justify-center p-8 text-center animate-in fade-in duration-500" style={{
      backgroundColor: welcomeConfig.backgroundColor,
      color: welcomeConfig.textColor,
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }} data-id="element-5601">
          {welcomeConfig.imageUrl && <img src={welcomeConfig.imageUrl} alt="Welcome" className="max-w-full h-auto max-h-[40vh] object-cover mb-8 rounded-lg shadow-lg" data-id="element-5602" />}

          {welcomeConfig.title && <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight" data-id="element-5603">
              {welcomeConfig.title}
            </h1>}

          {welcomeConfig.description && <p className="text-lg md:text-xl opacity-90 max-w-2xl mb-10 leading-relaxed whitespace-pre-wrap" data-id="element-5604">
              {welcomeConfig.description}
            </p>}

          <button onClick={handleStartForm} className="px-12 py-4 text-lg font-semibold rounded-lg shadow-md hover:scale-105 transition-transform" style={{
        backgroundColor: '#eab308',
        color: '#000000'
      }} data-id="element-5605">
            {welcomeConfig.buttonText || 'Join Us'}
          </button>

          {welcomeConfig.estimatedTime && <p className="mt-4 text-sm opacity-70 flex items-center gap-2" data-id="element-5606">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-5607">
                <circle cx="12" cy="12" r="10" data-id="element-5608"></circle>
                <polyline points="12 6 12 12 16 14" data-id="element-5609"></polyline>
              </svg>
              Takes {welcomeConfig.estimatedTime}
            </p>}
        </div>}

      {showForm && <div id={`form-52s-${slug}`} className="animate-in fade-in duration-500" dangerouslySetInnerHTML={{
      __html: record.HTML || ''
    }} data-id="element-5610" />}
    </>;
}